/**
 * "What's New" announcement modal — a lightweight one-per-feature nudge
 * shown on Home after we ship something worth telling users about.
 *
 * Model:
 *   - `ANNOUNCEMENTS` is an ordered list of announcement objects. Each
 *     has a stable `id`; once a user dismisses (or CTA-taps) an id, we
 *     stash it in localStorage and never re-show that one.
 *   - On Home paint we walk the list top-to-bottom and show the FIRST
 *     unseen entry. New announcements land at the top; oldest ones at
 *     the bottom get retired quietly.
 *   - Adding a new announcement is a one-line change here — no backend
 *     wiring, no migration, no admin toggle. If we later want cross-
 *     device sync, we can promote the seen-set to a `users` column;
 *     for now local-only is the simplest thing that works.
 *
 * UX rules of thumb:
 *   - Only shows AFTER the first tour is completed (`me.tour_completed`)
 *     — a first-time user gets the coachmark tour before an announcement
 *     nag on top of it.
 *   - Never stacks on top of an active tour or an open sheet.
 *   - Body copy stays short — this is a nudge, not a manual.
 */

import { navigate } from '../router.js';

const LOCAL_SEEN_KEY = 'triviana.whats_new_seen_v1';

// Add new entries at the TOP. Each announcement stays visible until
// the user dismisses it or taps the CTA — both dismiss it permanently
// on this device via localStorage.
const ANNOUNCEMENTS = [
  {
    id: 'async-duels-2026-07',
    emoji: '⚔️',
    tag: 'NEW · 1-v-1 duels',
    title: 'Win real money on your schedule',
    body: (
      "Publish a 1-v-1 duel any time — pick a topic, set the stake, " +
      "and someone will match you within 24 hours. Winner takes the pot. " +
      "No more waiting for the next scheduled live trivia."
    ),
    cta: { label: 'Publish a duel', href: '/duels/new' },
    // Gate: only show if the async-duels feature flag is on. The Home
    // strip already renders empty when the flag is off; nudging users
    // toward a feature that's not live would be embarrassing.
    condition: ({ payload }) => payload?.feature_enabled === true,
  },
];

let _openModal = null;

function readSeen() {
  try {
    const raw = localStorage.getItem(LOCAL_SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function markSeen(id) {
  try {
    const seen = readSeen();
    seen.add(id);
    localStorage.setItem(LOCAL_SEEN_KEY, JSON.stringify([...seen]));
  } catch {
    /* ignore — worst case they see it again next visit */
  }
}

/**
 * Entry point. Call from Home's success-paint path, AFTER the coach
 * tour has had a chance to fire. Bails silently if:
 *   - a modal is already open
 *   - the user hasn't finished the first tour (avoid stacking)
 *   - every announcement is either seen or gated off
 *
 * `duelsHomePayload` is optional; when provided it's passed to
 * announcement.condition() so per-feature gates (e.g. "flag is on")
 * can veto the display.
 */
export function maybeShowWhatsNew({ me, duelsHomePayload } = {}) {
  if (_openModal) return;
  if (!me) return;
  // Piggyback on the same "user is fully onboarded" gate as the
  // power-ups tour uses. We don't want to nag brand-new users mid-
  // coachmark.
  if (!me.tour_completed) return;
  const seen = readSeen();
  const ann = ANNOUNCEMENTS.find((a) => {
    if (seen.has(a.id)) return false;
    if (typeof a.condition === 'function') {
      try {
        if (!a.condition({ me, payload: duelsHomePayload })) return false;
      } catch {
        return false;
      }
    }
    return true;
  });
  if (!ann) return;
  showModal(ann);
}

function showModal(ann) {
  if (_openModal) return;
  const overlay = document.createElement('div');
  overlay.className = 'whats-new-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', ann.title);
  overlay.innerHTML = `
    <div class="whats-new-backdrop" data-wn-dismiss></div>
    <div class="whats-new-card" tabindex="-1">
      <button type="button" class="whats-new-close" aria-label="Close" data-wn-dismiss>✕</button>
      <div class="whats-new-emoji" aria-hidden="true">${escape(ann.emoji || '✨')}</div>
      <div class="whats-new-tag">${escape(ann.tag || "What's new")}</div>
      <div class="whats-new-title">${escape(ann.title)}</div>
      <div class="whats-new-body">${escape(ann.body)}</div>
      <div class="whats-new-actions">
        <button type="button" class="btn ghost" data-wn-dismiss>Later</button>
        ${ann.cta
          ? `<button type="button" class="btn teal" data-wn-cta>${escape(ann.cta.label)}</button>`
          : ''}
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  // Force reflow so the CSS transition kicks in on the next tick.
  // eslint-disable-next-line no-unused-expressions
  overlay.offsetHeight;
  overlay.classList.add('open');
  _openModal = { ann, overlay, onKey: null };

  const close = ({ ctaTaken = false } = {}) => {
    if (!_openModal || _openModal.overlay !== overlay) return;
    markSeen(ann.id);
    _openModal = null;
    document.removeEventListener('keydown', onKey, true);
    overlay.classList.remove('open');
    // Give the fade-out a beat before removing the node.
    setTimeout(() => overlay.remove(), 180);
    if (ctaTaken && ann.cta?.href) {
      navigate(ann.cta.href);
    }
  };

  overlay.querySelectorAll('[data-wn-dismiss]').forEach((el) => {
    el.addEventListener('click', (e) => {
      if (e.target !== e.currentTarget && e.currentTarget.classList.contains('whats-new-backdrop')) {
        // Only the backdrop itself triggers dismiss, not children
        // (defensive — but our backdrop has no children, so this is
        // belt-and-braces).
      }
      close();
    });
  });
  overlay.querySelectorAll('[data-wn-cta]').forEach((btn) => {
    btn.addEventListener('click', () => close({ ctaTaken: true }));
  });

  const onKey = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', onKey, true);
  _openModal.onKey = onKey;

  // Focus the card so keyboard users can Tab straight to the buttons.
  const card = overlay.querySelector('.whats-new-card');
  if (card) card.focus();
}

// Small local escaper so this module has zero internal imports beyond
// the router. Same pattern as the tour engine.
function escape(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
